<template>
  <!-- App Header -->
  <v-container fluid class="pa-0">
    <v-sheet
        color="primary"
        class="py-8 mb-8"
        rounded="0"
        elevation="1"
    >
    <v-row justify="center" align-items="center" class="text-center mt-2 ga-2">
        <v-icon icon="mdi-account-multiple-plus" size="56" color="white"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('generatepersonas.buildYourPersonas') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">
          {{ isTelcoDomain
            ? t('generatepersonas.fillAttributesToBuildAIPoweredPersonas')
            : t('generatepersonas.fillAttributesToCreatePersona') }}
        </div>
      </v-row>
    </v-sheet>
  </v-container>

  <v-container>
    <v-card class="pa-4">
      <div class="toolbar-row py-2 px-2">
        <div class="toolbar-domain">
          <v-select
              v-model="selectedDomain"
              :items="domainOptions"
              item-title="title"
              item-value="value"
              :label="$t('generatepersonas.selectDomain')"
              variant="outlined"
              density="compact"
              hide-details
              prepend-inner-icon="mdi-domain"
              @update:modelValue="onDomainChange"
          ></v-select>
        </div>

        <div v-if="showTelcoSegmentToolbar" class="toolbar-segment">
          <v-select
              :model-value="telcoSegmentSelectValue"
              :items="telcoSegmentsCampo.range"
              :label="$t('generatepersonas.selectSegment')"
              :menu-props="{ contentClass: 'segment-select-menu' }"
              variant="outlined"
              density="compact"
              hide-details
              :disabled="fieldsLoading || !telcoSegmentsCampo.editable"
              :clearable="telcoSegmentsCampo.editable"
              class="segment-select"
              @update:model-value="onTelcoSegmentChange"
          ></v-select>
          <v-tooltip
              :text="$t('generatepersonas.theSelectedSegmentDefinesTheArchetypeOfThePersona')"
              location="bottom"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                  icon="mdi-information-outline"
                  variant="text"
                  density="comfortable"
                  class="segment-info-btn"
                  v-bind="props"
              ></v-btn>
            </template>
          </v-tooltip>
        </div>

        <div class="toolbar-actions">
          <v-btn
              prepend-icon="mdi-eye"
              color="secondary"
              variant="flat"
              rounded="pill"
              :disabled="!canReview"
              @click="openReviewDialog"
          >{{ t('generatepersonas.review') }}</v-btn>
          <v-btn
              prepend-icon="mdi-broom"
              color="grey-darken-1"
              variant="tonal"
              rounded="pill"
              @click="clearSelections"
          >{{ t('generic.clear') }}</v-btn>
          <v-btn
              :prepend-icon="isTelcoDomain ? 'mdi-account-plus' : 'mdi-content-save-outline'"
              variant="flat"
              color="primary"
              rounded="pill"
              :loading="loading"
              :disabled="submitDisabled"
              @click="inviaForm"
          >{{ submitButtonLabel }}</v-btn>
        </div>
      </div>

      <v-row class="px-4 py-2 align-center ma-0">
        <v-icon icon="mdi-tune" color="primary" class="mr-2"></v-icon>
        <span class="text-h6 font-weight-bold">{{ t('generatepersonas.personaAttributes') }}</span>
        <v-progress-circular
            v-if="fieldsLoading"
            indeterminate
            size="20"
            width="2"
            color="primary"
            class="ml-3"
        ></v-progress-circular>
      </v-row>
      <v-divider class="mb-2"></v-divider>

      <div class="build-wrapper">
        <div>
          <p class="text-caption text-medium-emphasis px-2 mb-1 font-weight-medium" style="text-transform: uppercase; letter-spacing: 0.08em">{{ t('generatepersonas.configureAttributes') }}</p>
          <v-form ref="form" v-model="formValid">
          <div class="input-container">
            <div
                v-for="campo in visibleCampi"
                :key="campo.name"
                class="py-2"
            >
              <v-combobox
                  v-if="campo.name === 'active_services' && campo.editable"
                  v-model="formData[campo.name]"
                  multiple
                  chips
                  closable-chips
                  :label="fieldLabel(campo) + (isFieldRequired(campo) ? '*' : '')"
                  :rules="fieldRules(campo)"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
              />

              <v-text-field
                  v-else-if="campo.name === 'active_services' && !campo.editable"
                  :model-value="activeServicesDisplay(campo.name)"
                  :label="fieldLabel(campo) + (isFieldRequired(campo) ? '*' : '')"
                  readonly
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
              />

              <v-select
                  v-else-if="campo.range !== null"
                  v-model="formData[campo.name]"
                  :items="campo.range"
                  :label="fieldLabel(campo) + (isFieldRequired(campo) ? '*' : '')"
                  :rules="fieldRules(campo)"
                  :required="isFieldRequired(campo)"
                  :readonly="!campo.editable"
                  clearable
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
              />

              <v-text-field
                  v-else-if="isNumberField(campo)"
                  v-model.number="formData[campo.name]"
                  type="number"
                  :min="numberFieldBounds(campo).min"
                  :max="numberFieldBounds(campo).max"
                  :label="fieldLabel(campo) + (isFieldRequired(campo) ? '*' : '')"
                  :rules="fieldRules(campo)"
                  :required="isFieldRequired(campo)"
                  :readonly="!campo.editable"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
              />

              <v-text-field
                  v-else
                  v-model="formData[campo.name]"
                  :label="fieldLabel(campo) + (isFieldRequired(campo) ? '*' : '')"
                  :rules="fieldRules(campo)"
                  :required="isFieldRequired(campo)"
                  :readonly="!campo.editable"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
              />
            </div>
          </div>
          </v-form>
        </div>

        <v-card variant="outlined" rounded="xl" class="build-review-wrapper d-flex flex-column">
          <v-card-title class="d-flex align-center px-4 pt-4 pb-2">
            <v-icon icon="mdi-eye-outline" color="primary" class="mr-2" size="20"></v-icon>
            <span class="text-subtitle-1 font-weight-bold">{{ t('generatepersonas.livePreview') }}</span>
          </v-card-title>
          <v-divider></v-divider>

          <div
            v-if="!hasPreviewValues"
            class="d-flex flex-column align-center justify-center flex-grow-1 text-center pa-6"
          >
            <v-icon icon="mdi-clipboard-text-outline" size="48" color="grey-lighten-1" class="mb-3"></v-icon>
            <span class="text-body-2 text-medium-emphasis">{{ t('generatepersonas.fillInTheFieldsToSeeAPreview') }}</span>
          </div>

          <div class="build-review" v-else>
            <div
                v-if="showTelcoSegmentToolbar && hasFieldValue('segments')"
                class="build-review-item"
            >
              <p class="text-caption text-primary font-weight-medium" style="text-transform: uppercase">
                {{ $t('generatepersonas.selectSegment') }}
              </p>
              <p class="font-weight-bold text-body-2">{{ truncateValue(formData.segments) }}</p>
            </div>
            <div
                v-for="(campo, index) in visibleCampi"
                :key="index"
                class="build-review-item"
            >
              <p class="text-caption text-primary font-weight-medium" style="text-transform: uppercase">{{ fieldLabel(campo) }}</p>
              <p class="font-weight-bold text-body-2" v-if="hasFieldValue(campo.name)">{{ truncateValue(previewValue(campo)) }}</p>
            </div>
          </div>
        </v-card>
      </div>
      <v-dialog
          v-model="show_review"
          max-width="700"
          persistent
      >
        <v-card class="rounded-xl elevation-8">
          <v-card-title class="d-flex justify-space-between align-center bg-primary text-white rounded-t-xl py-4 px-6">
            <div class="d-flex align-center">
              <v-icon icon="mdi-account-details" class="me-2"></v-icon>
              {{ reviewDialogTitle }}
            </div>
            <v-btn @click="closeReviewDialog" variant="text" density="comfortable" icon="mdi-close" color="white"></v-btn>
          </v-card-title>

          <v-card-text class="pa-6 pb-0">
            <div class="build-review-dialog">
              <div class="build-review-item"
                   v-for="(item, index) in review_persona"
                   :key="item.key + '-' + index"
              >
                <p class="text-caption text-primary font-weight-medium mb-1" style="text-transform: uppercase">{{ reviewFieldLabel(item.key) }}</p>
                <div v-if="Array.isArray(item.value)" class="d-flex flex-wrap ga-1 mt-1">
                  <v-chip
                      v-for="(sub_value, sub_index) in item.value"
                      :key="sub_index"
                      size="small"
                      variant="tonal"
                      color="primary"
                  >{{ sub_value }}</v-chip>
                </div>
                <p v-else class="font-weight-medium text-body-2">{{ item.value }}</p>
              </div>
            </div>
          </v-card-text>

          <v-divider class="mt-4"></v-divider>
          <v-card-actions class="d-flex justify-space-between pa-4">
            <v-btn
                v-if="persona_id && isAdmin"
                @click="deletePersona"
                prepend-icon="mdi-delete-outline"
                variant="tonal"
                color="error"
                rounded="pill"
                :loading="deleteLoading"
            >{{ t('generic.delete') }}</v-btn>
            <v-spacer v-else></v-spacer>
            <v-btn
                @click="confirmReviewDialog"
                prepend-icon="mdi-check-circle-outline"
                color="primary"
                variant="flat"
                rounded="pill"
                :loading="loading"
            >{{ t('generatepersonas.confirm') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>

    <v-snackbar v-model="errorSnackbar" color="error" location="bottom right" :timeout="4000">
      <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>{{ errorMessage }}
    </v-snackbar>
    <v-snackbar v-model="successSnackbar" color="success" location="bottom right" :timeout="4000">
      <v-icon class="mr-2">mdi-check-circle-outline</v-icon>{{ successMessage }}
    </v-snackbar>
  </v-container>
</template>


<script>
import {
  generate_persona,
  delete_persona,
  create_or_update_persona,
  GENERATED_PERSONA_TAGS,
  fetch_persona_domain_schema,
  build_domain_options,
  get_default_domain,
  get_current_user_from_storage,
} from "@/api.js";
import { useI18n } from 'vue-i18n';
import { expandSelectChoices } from '@/utils/selectChoices';
import {
  buildNumberFieldRules,
  resolveNumberFieldBounds,
} from '@/utils/personaFieldValidation';

/** Telco CRM schema uses this key; toolbar control is telco-only (no label/key pattern matching). */
const TELCO_TOOLBAR_SEGMENT_KEY = 'segments';

export default {
  setup() {
    const { t } = useI18n();

    return {
      t
    };
  },
  data() {
    return {
      selectedDomain: null,
      schemaLoadToken: 0,
      loading: false,
      fieldsLoading: false,
      formValid: false,
      formData: {},
      campi: [],
      schemaLabels: {},
      rules: {
        required: value => {
          if (Array.isArray(value)) {
            return value.length > 0 || this.t('personaedit.fieldRequired');
          }
          if (value === null || value === undefined) {
            return this.t('personaedit.fieldRequired');
          }
          if (typeof value === 'number') {
            return !Number.isNaN(value) || this.t('personaedit.fieldRequired');
          }
          return String(value).trim() !== '' || this.t('personaedit.fieldRequired');
        },
      },
      review_persona: null,
      reviewSource: null,
      pendingGeneratedPersona: null,
      show_review: false,
      persona_id: null,
      deleteLoading: false,
      errorSnackbar: false,
      errorMessage: "",
      successSnackbar: false,
      successMessage: "",
    };
  },
  computed: {
    domainOptions() {
      return build_domain_options(this.$t);
    },
    isTelcoDomain() {
      return this.selectedDomain === 'telco';
    },
    telcoSegmentsCampo() {
      if (!this.isTelcoDomain) {
        return null;
      }
      const campo = this.campi.find((field) => field.name === TELCO_TOOLBAR_SEGMENT_KEY);
      if (!campo?.range?.length) {
        return null;
      }
      return campo;
    },
    showTelcoSegmentToolbar() {
      return !!this.telcoSegmentsCampo;
    },
    telcoSegmentSelectValue() {
      const value = this.formData[TELCO_TOOLBAR_SEGMENT_KEY];
      return value === '' || value === null || value === undefined ? null : value;
    },
    visibleCampi() {
      if (!this.showTelcoSegmentToolbar) {
        return this.campi;
      }
      return this.campi.filter((campo) => campo.name !== TELCO_TOOLBAR_SEGMENT_KEY);
    },
    submitButtonLabel() {
      return this.isTelcoDomain
        ? this.t('generatepersonas.generatePersona')
        : this.t('generatepersonas.savePersona');
    },
    reviewDialogTitle() {
      return this.isTelcoDomain
        ? this.t('generatepersonas.reviewGeneratedPersona')
        : this.t('generatepersonas.reviewSavedPersona');
    },
    isAdmin() {
      return get_current_user_from_storage()?.is_admin === true;
    },
    submitDisabled() {
      return !this.areRequiredFieldsComplete() || this.loading || this.fieldsLoading;
    },
    hasPreviewValues() {
      const hasSegment = this.showTelcoSegmentToolbar && this.hasFieldValue(TELCO_TOOLBAR_SEGMENT_KEY);
      const hasField = this.campi.some((campo) => this.hasFieldValue(campo.name));
      return hasSegment || hasField;
    },
    canReview() {
      return this.hasPreviewValues && this.areRequiredFieldsComplete();
    },
  },
  methods: {
    onTelcoSegmentChange(value) {
      if (!this.showTelcoSegmentToolbar) {
        return;
      }
      this.formData[TELCO_TOOLBAR_SEGMENT_KEY] = value ?? '';
    },
    async deletePersona() {
      if (!this.persona_id || !this.isAdmin) {
        return;
      }
      this.deleteLoading = true;
      try {
        await delete_persona(this.persona_id);
        this.clearSelections();
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || this.t('personamanagement.editError');
        this.errorSnackbar = true;
      } finally {
        this.deleteLoading = false;
      }
    },
    fieldLabel(campo) {
      if (typeof campo === 'string') {
        return this.schemaLabels[campo] || this.formatLabel(campo);
      }
      if (campo?.label) return campo.label;
      return this.schemaLabels[campo?.name] || this.formatLabel(campo?.name || '');
    },
    reviewFieldLabel(key) {
      return this.schemaLabels[key] || this.formatLabel(key);
    },
    isFieldRequired(campo) {
      return !!campo?.mandatory;
    },
    numberFieldBounds(campo) {
      return resolveNumberFieldBounds(campo);
    },
    fieldRules(campo) {
      const rules = [];
      if (this.isFieldRequired(campo)) {
        rules.push(this.rules.required);
      }
      if (this.isNumberField(campo)) {
        rules.push(...buildNumberFieldRules(campo, this.t));
      }
      return rules;
    },
    areRequiredFieldsComplete() {
      return this.campi.every((campo) => {
        if (!this.isFieldRequired(campo)) {
          return true;
        }
        return this.hasFieldValue(campo.name);
      });
    },
    isNumberField(campo) {
      return campo?.schemaType === 'number' || campo?.type === 'number';
    },
    parseActiveServices(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === 'string') {
        return value.split(',').map((entry) => entry.trim()).filter(Boolean);
      }
      return [];
    },
    activeServicesDisplay(name) {
      const value = this.formData[name];
      const items = this.parseActiveServices(value);
      return items.length ? items.join(', ') : '';
    },
    formatLabel(name) {
      return name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
    },
    truncateValue(value, maxLength = 20) {
      if (!value) return '';
      const str = String(value);
      return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
    },
    hasFieldValue(name) {
      const value = this.formData[name];
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    },
    clearSelections() {
      this.show_review = false;
      this.review_persona = null;
      this.reviewSource = null;
      this.pendingGeneratedPersona = null;
      this.persona_id = null;

      sessionStorage.removeItem('pending_generated_persona');
      sessionStorage.removeItem('user_selections');

      this.resetFormData();

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    resetFormData() {
      const nuovoFormData = {};
      this.campi.forEach((campo) => {
        nuovoFormData[campo.name] = this.initialFieldValue(campo);
      });
      this.formData = nuovoFormData;
    },
    async onDomainChange() {
      sessionStorage.setItem('generate_selected_domain', this.selectedDomain || '');
      this.show_review = false;
      this.review_persona = null;
      this.reviewSource = null;
      this.pendingGeneratedPersona = null;
      this.persona_id = null;
      sessionStorage.removeItem('pending_generated_persona');
      sessionStorage.removeItem('user_selections');
      await this.loadDomainSchemaFields();
    },
    resolveInitialDomain() {
      const allowed = this.domainOptions.map((domain) => domain.value);
      const fromRoute = this.$route.query.domain;
      const storedDomain = sessionStorage.getItem('generate_selected_domain');

      if (fromRoute && allowed.includes(fromRoute)) {
        return fromRoute;
      }
      if (storedDomain && allowed.includes(storedDomain)) {
        return storedDomain;
      }
      return get_default_domain();
    },
    isSchemaLoadCurrent(domain, loadToken) {
      return loadToken === this.schemaLoadToken
        && domain === this.selectedDomain;
    },
    async loadDomainSchemaFields() {
      if (!this.selectedDomain) {
        this.campi = [];
        this.schemaLabels = {};
        this.formData = {};
        return;
      }

      const domain = this.selectedDomain;
      const loadToken = ++this.schemaLoadToken;

      this.fieldsLoading = true;
      this.campi = [];
      this.schemaLabels = {};
      try {
        const response = await fetch_persona_domain_schema(domain);
        if (!this.isSchemaLoadCurrent(domain, loadToken)) {
          return;
        }

        const attributes = [...(response.data?.attributes || [])].sort(
          (a, b) => (a.display_order || 0) - (b.display_order || 0),
        );

        this.schemaLabels = Object.fromEntries(
          attributes
            .filter((attr) => attr.key && attr.label)
            .map((attr) => [attr.key, attr.label]),
        );

        this.campi = attributes
          .filter((attr) => attr.key)
          .map((attr) => this.attributeToCampo(attr));

        this.resetFormData();
      } catch (error) {
        if (!this.isSchemaLoadCurrent(domain, loadToken)) {
          return;
        }
        this.campi = [];
        this.schemaLabels = {};
        this.formData = {};
        this.errorMessage = error?.response?.data?.detail || this.t('generatepersonas.schemaLoadFailed');
        this.errorSnackbar = true;
      } finally {
        if (this.isSchemaLoadCurrent(domain, loadToken)) {
          this.fieldsLoading = false;
        }
      }
    },
    attributeToCampo(attr) {
      const choices = attr.type === 'single_select'
        ? expandSelectChoices(attr.options?.choices)
        : [];
      const editable = attr.editable !== false;
      return {
        name: attr.key,
        label: attr.label || this.formatLabel(attr.key),
        schemaType: attr.type,
        type: attr.type === 'number' ? 'number' : 'string',
        displayOrder: attr.display_order || 0,
        range: choices.length ? choices : null,
        mandatory: !!attr.required,
        editable,
        min: attr.options?.min ?? attr.options?.min_value ?? undefined,
        max: attr.options?.max ?? attr.options?.max_value ?? undefined,
      };
    },
    initialFieldValue(campo) {
      if (campo.name === 'active_services') {
        return [];
      }
      return this.isNumberField(campo) ? null : '';
    },
    serializeFieldValue(campo, value) {
      if (campo.name === 'active_services') {
        const items = this.parseActiveServices(value);
        return items.length ? items.join(',') : undefined;
      }
      if (this.isNumberField(campo)) {
        const numeric = Number(value);
        return Number.isNaN(numeric) ? value : numeric;
      }
      return value;
    },
    buildPersonaDataForSave() {
      const data = {};

      for (const campo of this.campi) {
        const value = this.formData[campo.name];
        if (value === '' || value === null || value === undefined) {
          continue;
        }
        if (Array.isArray(value) && !value.length) {
          continue;
        }
        const serialized = this.serializeFieldValue(campo, value);
        if (serialized === undefined) {
          continue;
        }
        data[campo.name] = serialized;
      }

      return data;
    },
    buildFormPayload() {
      const payload = {};

      for (const campo of this.campi) {
        const value = this.formData[campo.name];
        if (value === '' || value === null || value === undefined) {
          continue;
        }
        if (Array.isArray(value) && !value.length) {
          continue;
        }
        const serialized = this.serializeFieldValue(campo, value);
        if (serialized === undefined) {
          continue;
        }
        payload[campo.name] = serialized;
      }

      return payload;
    },
    previewValue(campo) {
      const value = this.formData[campo.name];
      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '';
      }
      return value;
    },
    derivePersonaName(data) {
      const first = data.first_name || data.name || '';
      const last = data.last_name || data.surname || '';
      const full = `${first} ${last}`.trim();
      if (full) return full;

      const firstTextField = this.campi.find(
        (campo) => campo.type !== 'number' && !campo.range && this.hasFieldValue(campo.name),
      );
      if (firstTextField) {
        return String(data[firstTextField.name]).trim();
      }

      return '';
    },
    setReviewFromData(data) {
      this.review_persona = Object.entries(data)
        .filter(([key]) => key !== 'personal_characteristics' && key !== 'personalCharacteristics')
        .map(([key, value]) => ({
          key,
          value,
        }));
    },
    async openReviewDialog() {
      const valid = await this.validateForm();
      if (!valid) {
        return;
      }
      this.setReviewFromData(this.buildPersonaDataForSave());
      this.reviewSource = 'preview';
      this.show_review = true;
    },
    closeReviewDialog() {
      this.show_review = false;
      if (this.reviewSource === 'preview' || this.reviewSource === 'save') {
        this.reviewSource = null;
        this.review_persona = null;
        return;
      }
      if (this.reviewSource === 'generate') {
        this.reviewSource = null;
        this.review_persona = null;
        this.pendingGeneratedPersona = null;
        sessionStorage.removeItem('pending_generated_persona');
      }
    },
    async validateForm() {
      const result = await this.$refs.form.validate();
      const valid = result === true || (result && result.valid === true);
      if (!valid) {
        this.errorMessage = this.t('generatepersonas.fillRequiredAttributes');
        this.errorSnackbar = true;
      }
      return valid;
    },
    async inviaForm() {
      const valid = await this.validateForm();
      if (!valid) {
        return;
      }

      try {
        if (this.isTelcoDomain) {
          this.loading = true;
          await this.generateTelcoPersona();
        } else {
          this.showSaveReviewDialog();
        }
      } catch (error) {
        this.review_persona = null;
        this.reviewSource = null;
        this.errorMessage = error?.response?.data?.detail || this.t('personamanagement.editError');
        this.errorSnackbar = true;
      } finally {
        this.loading = false;
      }
    },
    async generateTelcoPersona() {
      const response = await generate_persona(
        this.selectedDomain,
        this.buildFormPayload(),
        false,
      );
      const persona = response.data.persona?.data;
      if (!persona) {
        throw new Error('Generated persona data is missing');
      }

      this.pendingGeneratedPersona = persona;
      this.persona_id = null;
      this.setReviewFromData(persona);

      sessionStorage.setItem('pending_generated_persona', JSON.stringify(persona));
      sessionStorage.setItem('user_selections', JSON.stringify(this.formData));
      sessionStorage.setItem('generate_selected_domain', this.selectedDomain);

      this.reviewSource = 'generate';
      this.show_review = true;
    },
    showSaveReviewDialog() {
      const personaData = this.buildPersonaDataForSave();
      this.setReviewFromData(personaData);
      this.reviewSource = 'save';
      this.show_review = true;
    },
    async confirmReviewDialog() {
      if (this.reviewSource === 'save') {
        await this.saveManualPersona();
        return;
      }

      if (this.reviewSource === 'generate') {
        await this.saveGeneratedPersona();
        return;
      }

      this.show_review = false;
      this.reviewSource = null;
    },
    async saveGeneratedPersona() {
      const personaData = this.pendingGeneratedPersona;
      if (!personaData) {
        this.errorMessage = this.t('personamanagement.editError');
        this.errorSnackbar = true;
        return;
      }

      const name = this.derivePersonaName(personaData) || null;
      this.loading = true;
      try {
        const response = await create_or_update_persona(
          name,
          this.selectedDomain,
          personaData,
          null,
          GENERATED_PERSONA_TAGS,
        );
        this.persona_id = response.data.persona_id;

        this.successMessage = this.t('generatepersonas.generateSuccess');
        this.successSnackbar = true;

        const domain = this.selectedDomain;
        this.clearSelections();
        if (domain) {
          this.$router.push({ name: 'persona', query: { domain, view: 'generated' } });
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || this.t('personamanagement.editError');
        this.errorSnackbar = true;
      } finally {
        this.loading = false;
      }
    },
    async saveManualPersona() {
      const personaData = this.buildPersonaDataForSave();
      const name = this.derivePersonaName(personaData) || null;

      this.loading = true;
      try {
        await create_or_update_persona(
          name,
          this.selectedDomain,
          personaData,
          null,
          GENERATED_PERSONA_TAGS,
        );

        this.successMessage = this.t('generatepersonas.saveSuccess');
        this.successSnackbar = true;

        const domain = this.selectedDomain;
        this.clearSelections();
        if (domain) {
          this.$router.push({ name: 'persona', query: { domain, view: 'generated' } });
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || this.t('personamanagement.editError');
        this.errorSnackbar = true;
      } finally {
        this.loading = false;
      }
    },
    restoreSessionState() {
      const storedDomain = sessionStorage.getItem('generate_selected_domain');
      if (storedDomain && storedDomain !== this.selectedDomain) {
        return;
      }

      try {
        const storedGenerated = sessionStorage.getItem('pending_generated_persona');
        if (storedGenerated) {
          this.pendingGeneratedPersona = JSON.parse(storedGenerated);
          this.setReviewFromData(this.pendingGeneratedPersona);
          this.reviewSource = 'generate';
          this.show_review = true;
        }
      } catch {
        this.pendingGeneratedPersona = null;
      }

      try {
        const storedSelections = sessionStorage.getItem('user_selections');
        if (storedSelections) {
          this.formData = { ...this.formData, ...JSON.parse(storedSelections) };
        }
      } catch {
        // Ignore invalid session data.
      }
    },
  },
  watch: {
    '$route.query.domain'(domain) {
      const allowed = this.domainOptions.map((option) => option.value);
      if (!domain || !allowed.includes(domain) || domain === this.selectedDomain) {
        return;
      }
      this.selectedDomain = domain;
      this.onDomainChange();
    },
  },
  async mounted() {
    this.selectedDomain = this.resolveInitialDomain();
    await this.loadDomainSchemaFields();
    this.restoreSessionState();
  },
};
</script>


<style scoped>
.toolbar-row {
  display: grid;
  grid-template-columns: minmax(12rem, 16rem) minmax(16rem, 1fr) auto;
  align-items: center;
  column-gap: 1rem;
  row-gap: 0.75rem;
  min-height: 3.5rem;
}

.toolbar-row:not(:has(.toolbar-segment)) {
  grid-template-columns: minmax(12rem, 16rem) auto;
}

.toolbar-domain,
.toolbar-segment,
.toolbar-actions {
  min-width: 0;
}

.toolbar-segment {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.25rem;
  min-height: 2.5rem;
}

.toolbar-segment .segment-select {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.toolbar-segment .segment-info-btn {
  flex: 0 0 auto;
}

.toolbar-actions {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  align-self: center;
}

.segment-select :deep(.v-field__input) {
  justify-content: flex-start;
  text-align: left;
}

.segment-select :deep(.v-select__selection) {
  justify-content: flex-start;
  width: 100%;
  margin-inline-start: 0;
}

.segment-select :deep(.v-select__selection-text) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

:global(.segment-select-menu .v-list-item-title) {
  text-align: left;
  white-space: normal;
}

@media (max-width: 960px) {
  .toolbar-row,
  .toolbar-row:not(:has(.toolbar-segment)) {
    grid-template-columns: 1fr;
  }

  .toolbar-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

.build-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

:deep(.v-field:has(input:read-only)) {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}

:deep(.v-field) input:read-only {
  opacity: var(--v-disabled-opacity);
  cursor: not-allowed;
}

.input-container {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #005B96 transparent;
  margin-bottom: 0.5rem;
  height: calc(100vh - 30rem);
}

.build-review-wrapper {
  overflow: hidden;
  height: calc(100vh - 30rem);
}

.build-review {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0.75rem;
  gap: 0.75rem;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #005B96 transparent;
  height: calc(100vh - 34rem);

  .build-review-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
}

.build-review-dialog {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 0.5rem 0;

  .build-review-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
